import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sessionsApi, cashflowApi } from '../api/endpoints';
import { useAuthStore } from '../stores/auth.store';
import type { Cashflow, Expense } from '@motra/shared';

export function useSessionStatus(shopId?: string) {
  const shop = useAuthStore((s) => s.shop);
  const activeShopId = shopId ?? shop?.id;

  return useQuery({
    queryKey: ['session-status', activeShopId],
    queryFn: () => sessionsApi.status(activeShopId!),
    enabled: !!activeShopId,
    select: (res) => res.data ?? null,
    refetchInterval: 1000 * 30, // refetch every 30s
  });
}

export function useToggleSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      shopid: string;
      action: 'open' | 'close';
      opening_cash?: number;
      closing_cash?: number;
    }) => sessionsApi.toggle(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['session-status'] });
      queryClient.invalidateQueries({ queryKey: ['session-summary'] });
    },
  });
}

export function useSessionSummary(sessionId?: string) {
  return useQuery({
    queryKey: ['session-summary', sessionId],
    queryFn: () => sessionsApi.summary(sessionId!),
    enabled: !!sessionId,
    select: (res) => res.data,
  });
}

export function useCashflow(sessionId?: string) {
  const shop = useAuthStore((s) => s.shop);

  return useQuery({
    queryKey: ['cashflow', sessionId],
    queryFn: () => cashflowApi.list(shop!.id, sessionId),
    enabled: !!shop?.id,
    select: (res) => res.data ?? [],
  });
}

export function useAddCashflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Cashflow> & { shopid: string }) =>
      cashflowApi.add(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cashflow'] });
    },
  });
}

export function useAddExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Expense> & { shopid: string }) =>
      cashflowApi.addExpense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}

export function useExpenses(sessionId?: string) {
  const shop = useAuthStore((s) => s.shop);

  return useQuery({
    queryKey: ['expenses', sessionId],
    queryFn: () => cashflowApi.listExpenses(shop!.id, sessionId),
    enabled: !!shop?.id,
    select: (res) => res.data ?? [],
  });
}
